import { prepareFilterParam, prepareSelectParam } from '../lib/queryParams'
import { API_URL_NEW } from '../lib/endpoints'
import axios from 'axios';
import { ALLOWED_QUERY_OPERATOR, ENTITY_TYPE, FUNDING_STATUS, TAXONOMIES } from '../lib/constants';

const getPortfolioExitsFilters = (investorId, entityType) => prepareFilterParam([
    {
        key: 'funding_stage_type_filter',
        value: [TAXONOMIES.exitsFundingStage],
        op: ALLOWED_QUERY_OPERATOR.equal
    },
    {
        key: 'verification_status',
        value: [FUNDING_STATUS.draft],
        op: ALLOWED_QUERY_OPERATOR.notEq
    },
    {
        key: 'previous_investor_filter',
        value: [investorId],
        op: ALLOWED_QUERY_OPERATOR.equal,
    },
    {
        key: 'previous_investor_type_filter',
        value: [entityType],
        op: ALLOWED_QUERY_OPERATOR.equal,
    }
]);

const exitApi = {
    // Startup Directory Exit View
    getExitData: (selectableFields = [], filters, limit, page, sort, directoryType, signal) => {

        const fields = prepareSelectParam([
            'id', 'investedin_id', 'investedin_data', 'total_amount_raised_data',
            ...selectableFields
        ]);

        // get only exits stage funding rounds
        const arrFilters = [{
            key: 'funding_stage_type_filter',
            value: [TAXONOMIES.exitsFundingStage],
            op: ALLOWED_QUERY_OPERATOR.equal
        }];

        if (filters.indexOf('verification_status') === -1) {
            arrFilters.push({ key: 'verification_status', value: [FUNDING_STATUS.draft], op: ALLOWED_QUERY_OPERATOR.notEq }) // exclude draft funding rounds
        }

        if (!filters?.includes('investedin_type_filter')) {
            if (directoryType.toLowerCase() == ENTITY_TYPE.startup.toLowerCase()) {
                arrFilters.push({ key: 'investedin_type_filter', value: [ENTITY_TYPE.startup], op: ALLOWED_QUERY_OPERATOR.equal }) // get only startup exits
            } else if (directoryType.toLowerCase() == ENTITY_TYPE.company.toLowerCase()) {
                arrFilters.push({ key: 'investedin_type_filter', value: [ENTITY_TYPE.startup, ENTITY_TYPE.corporate], op: ALLOWED_QUERY_OPERATOR.equal }) // get startup & corporate exits
            }
        }

        const permanentFilters = prepareFilterParam(arrFilters);

        const combinedFilters = filters ? `${filters}^,${permanentFilters}` : permanentFilters;
        // Pure Startup directory uses funding-breakdown directly; Company/Client Directory need
        // companies-funding's startup+corporate join (PR #289 review decision).
        const isStartupDirectory = directoryType?.toLowerCase() === ENTITY_TYPE.startup.toLowerCase();
        const endpoint = isStartupDirectory ? 'funding-breakdown' : 'companies-funding';
        const url = `${API_URL_NEW}/${endpoint}?fields=${fields}&filters=${combinedFilters}&limit=${limit}&page=${page}&sort=${sort}`;

        return new Promise((resolve, reject) => {

            axios.get(url, { signal }).then((response) => {
                if (response.data.data && response.data.data.length > 0) {
                    resolve({
                        exits: response.data.data,
                        totalRecords: response.data.navigation.total
                    });
                } else {
                    resolve({
                        exits: [],
                        totalRecords: 0
                    });
                }
            }).catch((err) => reject(err));
        });
    },

    // Startup Directory Exit View
    getExitDataCount: (filters, directoryType, signal) => {
        const arrFilters = [
            { key: 'funding_stage_type_filter', value: [TAXONOMIES.exitsFundingStage], op: ALLOWED_QUERY_OPERATOR.equal }, // get only exits stage funding rounds
        ];

        if (filters.indexOf('verification_status') === -1) {
            arrFilters.push({ key: 'verification_status', value: [FUNDING_STATUS.draft], op: ALLOWED_QUERY_OPERATOR.notEq }) // exclude draft funding rounds
        }

        if (!filters?.includes('investedin_type_filter')) {
            if (directoryType.toLowerCase() == ENTITY_TYPE.startup.toLowerCase()) {
                arrFilters.push({ key: 'investedin_type_filter', value: [ENTITY_TYPE.startup], op: ALLOWED_QUERY_OPERATOR.equal }) // get only startup exits
            } else if (directoryType.toLowerCase() == ENTITY_TYPE.company.toLowerCase()) {
                arrFilters.push({ key: 'investedin_type_filter', value: [ENTITY_TYPE.startup, ENTITY_TYPE.corporate], op: ALLOWED_QUERY_OPERATOR.equal }) // get startup & corporate exits
            }
        }

        const permanentFilters = prepareFilterParam(arrFilters);

        const combinedFilters = filters ? `${filters}^,${permanentFilters}` : permanentFilters;
        const url = `${API_URL_NEW}/funding/setting/count?filters=${combinedFilters}`;

        return new Promise((resolve, reject) => {

            axios.get(url, { signal }).then((response) => {
                if (response.data.status === 200) {
                    resolve({
                        totalRecords: response.data.data,
                    });
                } else {
                    resolve([]);
                }
            }).catch((err) => reject(err));
        });
    },

    // get total exits (M&A, IPO's etc.)
    getExitsCount: () => {
        const permanentFilters = prepareFilterParam([
            { key: 'funding_stage_type_filter', value: [TAXONOMIES.exitsFundingStage], op: ALLOWED_QUERY_OPERATOR.equal }, // get only exits stage funding rounds
            { key: 'verification_status', value: [FUNDING_STATUS.draft], op: ALLOWED_QUERY_OPERATOR.notEq } // exclude draft funding rounds
        ]);

        return new Promise((resolve, reject) => {
            let url = `${API_URL_NEW}/funding/setting/count?filters=${permanentFilters}`;
            axios.get(url).then(async (response) => {
                if (response.data.status === 200) {
                    resolve(response.data.data);
                } else {
                    let error = new Error(response);
                    throw error;
                }
            }).catch(err => {
                let data = {
                    error: err,
                    // set static value in case of error
                    totalCount: "77"
                }
                console.log("ERROR: ", err);
                reject(data);
            })
        })
    },

    getPortfolioExitsCount: (investorId, entityType) => {
        const filters = getPortfolioExitsFilters(investorId, entityType);
        const url = `${API_URL_NEW}/funding-breakdown/setting/count?filters=${filters}`;

        return new Promise((resolve, reject) => {
            axios.get(url).then((response) => {
                if (response.data.status === 200) {
                    resolve({
                        totalRecords: response.data.data,
                    });
                } else {
                    resolve({
                        totalRecords: 0
                    });
                }
            }).catch((err) => reject(err));
        });
    },

    /*
     * get exits from an investor's portfolio (M&A, IPO's etc.)
     * (entities they invested in in the past, that have been acquired)
     */
    getPortfolioExits: (investorId, entityType, limit, page = 1) => {
        const filters = getPortfolioExitsFilters(investorId, entityType);

        const fields = prepareSelectParam([
            'id', 
            'investedin_data', 
            'primary_industry_data', 
            'location_data', 
            'date_raised', 
            'time_to_exit', 
            'investment_vehicle_data', 
            'amount', 
            'investors_data', 
            'previous_investors_data',
            'funding_stage_data',
            'total_amount_raised',
            'investedin_funding_count',
            'acquirer_type',
            'acquirer_hq_data'
        ]);

        const url = `${API_URL_NEW}/funding-breakdown?fields=${fields}&filters=${filters}&limit=${limit}&page=${page}&sort=date_raised^:DESC`;

        return new Promise((resolve, reject) => {
            axios.get(url).then((response) => {
                if (response.data.data && response.data.data.length > 0) {
                    resolve({
                        rounds: response.data.data,
                        totalRecords: response.data.navigation.total
                    });
                } else {
                    resolve({
                        rounds: [],
                        totalRecords: 0
                    });
                }
            }).catch((err) => reject(err));
        });

    }

}

export default exitApi;
